<script setup lang="ts">
import { ElConfigProvider, ElMessage } from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { aiActiveGenerationTasksApi, aiGenerationTaskApi } from '@/api/ai'
import { generationSocket } from '@/composables/useGenerationSocket'
import { useGenerationTaskStore } from '@/store/modules/generationTask'
import { useUserStore } from '@/store/modules/user'
import dayjs from '@/utils/dayjs'
import { normalizeActiveGenerationTasks, normalizeGenerationQuery } from '@/utils/generationTaskQuery'
import 'dayjs/locale/zh-cn'

const { locale } = useI18n()
locale.value = 'zh-cn'
dayjs.locale('zh-cn')
const generationTaskStore = useGenerationTaskStore()
const userStore = useUserStore()
let generationHealthTimer: ReturnType<typeof setInterval> | null = null
let generationQueryTimer: ReturnType<typeof setTimeout> | null = null
let generationQueryInFlight = false
let generationQueryAttempt = 0
let activeTaskQueryInFlight = false
let lastActiveTaskQueryAt = 0
const GENERATION_QUERY_DELAYS = [0, 2_000, 3_000, 5_000, 8_000, 12_000, 15_000]
/** 生成任务超过半小时仍未完成则停止轮询 */
const GENERATION_QUERY_MAX_MS = 30 * 60 * 1000

function isGenerationQueryTimedOut() {
  const startedAt = generationTaskStore.taskStartedAt
  if (!startedAt) {
    return false
  }
  return Date.now() - startedAt >= GENERATION_QUERY_MAX_MS
}

function cancelTimedOutGenerationQuery() {
  stopGenerationQuery()
  const type = (generationTaskStore.snapshot?.type || '').toLowerCase()
  const label = type === 'video' ? '视频' : type === 'audio' ? '音频' : '图片'
  generationTaskStore.fail(`${label}生成已超过半小时，已取消查询`)
}

async function recoverServerActiveTask() {
  if (
    !userStore.isLogin
    || activeTaskQueryInFlight
    || generationTaskStore.taskId
    || (generationTaskStore.isRunning && !generationTaskStore.taskId)
    || Date.now() - lastActiveTaskQueryAt < 15_000
  ) {
    return
  }
  activeTaskQueryInFlight = true
  lastActiveTaskQueryAt = Date.now()
  try {
    const response = await aiActiveGenerationTasksApi()
    const [latestTask] = normalizeActiveGenerationTasks(response)
    if (latestTask && !generationTaskStore.taskId) {
      const createdAt = Date.parse(latestTask.createdAt || '') || 0
      if (createdAt > 0 && Date.now() - createdAt >= GENERATION_QUERY_MAX_MS) {
        return
      }
      generationTaskStore.restoreServerTask(latestTask)
    }
  }
  catch {
    // 活动任务发现失败不影响正常使用；窗口再次获得焦点时会重试。
  }
  finally {
    activeTaskQueryInFlight = false
  }
}

function handleWindowFocus() {
  void recoverServerActiveTask()
}

function stopGenerationQuery() {
  if (generationQueryTimer) {
    clearTimeout(generationQueryTimer)
    generationQueryTimer = null
  }
  generationQueryAttempt = 0
}

function shouldQueryGenerationTask() {
  return userStore.isLogin && Boolean(generationTaskStore.taskId) && (
    generationTaskStore.status === 'connecting'
    || generationTaskStore.status === 'generating'
    || generationTaskStore.status === 'monitoring'
    || generationTaskStore.status === 'connection_lost'
  )
}

function scheduleGenerationQuery(delay?: number) {
  if (!shouldQueryGenerationTask() || generationQueryTimer || generationQueryInFlight) {
    return
  }
  if (isGenerationQueryTimedOut()) {
    cancelTimedOutGenerationQuery()
    return
  }
  const nextDelay = delay ?? GENERATION_QUERY_DELAYS[Math.min(
    generationQueryAttempt,
    GENERATION_QUERY_DELAYS.length - 1,
  )]
  generationQueryTimer = setTimeout(() => {
    generationQueryTimer = null
    queryGenerationTask()
  }, nextDelay)
}

async function queryGenerationTask() {
  if (!shouldQueryGenerationTask() || generationQueryInFlight) {
    return
  }
  if (isGenerationQueryTimedOut()) {
    cancelTimedOutGenerationQuery()
    return
  }
  const requestedTaskId = generationTaskStore.taskId
  generationQueryInFlight = true
  try {
    const response = await aiGenerationTaskApi(requestedTaskId)
    if (generationTaskStore.taskId !== requestedTaskId) {
      return
    }
    if (isGenerationQueryTimedOut()) {
      cancelTimedOutGenerationQuery()
      return
    }
    const result = normalizeGenerationQuery(response)
    if (result.state === 'completed') {
      generationTaskStore.complete(result.urls, result.credit)
      return
    }
    if (result.state === 'failed') {
      generationTaskStore.fail(result.message || '生成失败')
      return
    }
    generationTaskStore.markMonitoring()
  }
  catch {
    // 网络或鉴权的临时失败不应把后台任务误判为生成失败，继续退避查询。
    if (generationTaskStore.taskId === requestedTaskId) {
      generationTaskStore.markMonitoring()
    }
  }
  finally {
    generationQueryInFlight = false
    if (shouldQueryGenerationTask()) {
      generationQueryAttempt += 1
      scheduleGenerationQuery()
    }
  }
}

function handleBeforeUnload(event: BeforeUnloadEvent) {
  // 已拿到 task_id 后可以在刷新后恢复查询；仅保护尚未被后台受理的建连阶段。
  if (!generationTaskStore.isRunning || generationTaskStore.taskId) {
    return
  }
  event.preventDefault()
  event.returnValue = ''
}

watch(() => generationTaskStore.status, (status, previousStatus) => {
  if (status === 'completed' && previousStatus !== 'completed') {
    ElMessage.success('生成已完成，可前往创意生成查看')
  }
  else if (status === 'connection_lost' && previousStatus !== 'connection_lost') {
    ElMessage.warning('生成连接已断开，任务结果待确认')
  }
  else if (status === 'failed' && previousStatus !== 'failed') {
    ElMessage.error(generationTaskStore.errorMessage || '生成失败，可前往创意生成查看')
  }
})

watch(
  () => [generationTaskStore.taskId, generationTaskStore.status, userStore.isLogin] as const,
  () => {
    if (shouldQueryGenerationTask()) {
      scheduleGenerationQuery(0)
    }
    else {
      stopGenerationQuery()
    }
  },
)

watch(() => userStore.isLogin, (isLogin, wasLogin) => {
  if (isLogin && !wasLogin) {
    lastActiveTaskQueryAt = 0
    void recoverServerActiveTask()
  }
  if (!isLogin && wasLogin) {
    generationSocket.close()
    stopGenerationQuery()
    generationTaskStore.discardActiveTask()
  }
})

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('focus', handleWindowFocus)
  userStore.syncFromCookie()
  generationTaskStore.restorePersistedTask()
  if (isGenerationQueryTimedOut()) {
    cancelTimedOutGenerationQuery()
  }
  void recoverServerActiveTask()
  if (shouldQueryGenerationTask()) {
    scheduleGenerationQuery(0)
  }
  generationHealthTimer = setInterval(() => {
    if (generationTaskStore.status === 'generating' && !generationSocket.isConnected()) {
      if (generationTaskStore.taskId) {
        generationTaskStore.markMonitoring()
      }
      else {
        generationTaskStore.markConnectionLost('生成连接已失效，结果待确认')
      }
    }
  }, 2_000)
  // 被 iframe 嵌入时去掉 #app 的 min-width，避免横向滚动条
  if (window.self !== window.top) {
    document.documentElement.classList.add('in-iframe')
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('focus', handleWindowFocus)
  stopGenerationQuery()
  if (generationHealthTimer) {
    clearInterval(generationHealthTimer)
    generationHealthTimer = null
  }
})
</script>

<template>
  <ElConfigProvider :locale="zhCn">
    <RouterView />
    <FaToast />
  </ElConfigProvider>
</template>
